import React from "react";
import { MessageSquareReply, CheckCircle2, Image as ImageIcon } from "lucide-react";

const fmtDate = (d) =>
  d ? new Date(d).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" }) : "";
const fmtINR = (n) => (n == null ? null : `₹${Number(n).toLocaleString("en-IN")}`);

// Rough attachment count for a deliverable's media (section-keyed object OR flat array OR string).
const countMedia = (media) => {
  if (!media) return 0;
  if (Array.isArray(media)) return media.length;
  if (typeof media === "string") return 1;
  if (typeof media === "object") {
    return Object.values(media).reduce((s, v) => s + (Array.isArray(v) ? v.length : v ? 1 : 0), 0);
  }
  return 0;
};

function Bubble({ side, children }) {
  return (
    <div className={`flex ${side === "right" ? "justify-end" : "justify-start"}`}>
      <div className="max-w-[85%]">{children}</div>
    </div>
  );
}

/**
 * Chronological, chat-style view of the ID↔BV design loop, rendered directly off the lead's
 * deliverables[] (arch DeliverableThread parity — no separate conversation model).
 *
 * Props:
 *   deliverables[] — from GET /interior-designers/leads/{id}/detail (camelCased)
 *   perspective    — "DESIGNER" | "BV" (whose bubbles align right / read "You")
 *   onOpenMedia(deliverable) — optional; opens the deliverable's media in the host viewer
 */
function DesignConversation({ deliverables = [], perspective = "BV", onOpenMedia }) {
  if (!deliverables.length) {
    return <p className="text-sm text-gray-400">No design has been submitted yet.</p>;
  }
  const designerSide = perspective === "DESIGNER" ? "right" : "left";
  const reviewerSide = perspective === "DESIGNER" ? "left" : "right";
  const designerLabel = perspective === "DESIGNER" ? "You" : "Designer";
  const reviewerLabel = perspective === "DESIGNER" ? "Reviewer" : "You";

  return (
    <div className="space-y-3">
      {deliverables.map((d) => {
        const mediaCount = countMedia(d.media);
        const status = d.status;
        // Reviewer's attachments on a change request ([{ url, type, caption? }]).
        const feedbackMedia = Array.isArray(d.buyerFeedbackMedia)
          ? d.buyerFeedbackMedia.filter((m) => m && (m.url || m.src))
          : [];
        return (
          <React.Fragment key={d.designIterationId}>
            {/* Designer submission */}
            <Bubble side={designerSide}>
              <div className="rounded-lg border border-gray-200 bg-white p-3">
                <div className="mb-1 flex items-center justify-between gap-2">
                  <span className="text-xs font-semibold text-gray-500">
                    {designerLabel} · Version {d.iterationNumber}
                  </span>
                  {d.isFinal && (
                    <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-medium text-emerald-700">
                      Final design
                    </span>
                  )}
                </div>
                {d.designerNotes && (
                  <p className="whitespace-pre-wrap text-sm text-gray-700">{d.designerNotes}</p>
                )}
                <div className="mt-2 flex flex-wrap items-center gap-2">
                  {fmtINR(d.estimatedPrice) && (
                    <span className="text-xs font-medium text-gray-500">Estimate: {fmtINR(d.estimatedPrice)}</span>
                  )}
                  {mediaCount > 0 && (
                    <button
                      type="button"
                      onClick={() => onOpenMedia?.(d)}
                      className="inline-flex items-center gap-1 rounded-md bg-gray-100 px-2 py-1 text-xs text-gray-700 hover:bg-gray-200"
                    >
                      <ImageIcon size={12} /> {mediaCount} design file{mediaCount > 1 ? "s" : ""}
                    </button>
                  )}
                </div>
                <div className="mt-1 text-[11px] text-gray-400">{fmtDate(d.createdDate)}</div>
              </div>
            </Bubble>

            {/* Reviewer change-request (only when changes were requested on this version) */}
            {status === "CHANGES_REQUESTED" && (d.buyerFeedback || feedbackMedia.length > 0) && (
              <Bubble side={reviewerSide}>
                <div className="rounded-lg border border-orange-200 bg-orange-50 p-3">
                  <div className="mb-1 flex items-center gap-1 text-xs font-semibold text-orange-700">
                    <MessageSquareReply size={13} /> {reviewerLabel} requested changes
                  </div>
                  {d.buyerFeedback && (
                    <p className="whitespace-pre-wrap text-sm text-orange-800">{d.buyerFeedback}</p>
                  )}
                  {feedbackMedia.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-2">
                      {feedbackMedia.map((m, i) => {
                        const src = m.url || m.src;
                        return (
                          <a
                            key={i}
                            href={src}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={m.caption || "Open attachment"}
                            className="block"
                          >
                            <img
                              src={src}
                              alt={m.caption || `Attachment ${i + 1}`}
                              loading="lazy"
                              className="h-16 w-16 rounded-md border border-orange-200 object-cover hover:opacity-90"
                            />
                          </a>
                        );
                      })}
                    </div>
                  )}
                  {d.feedbackDate && (
                    <div className="mt-1 text-[11px] text-gray-400">{fmtDate(d.feedbackDate)}</div>
                  )}
                </div>
              </Bubble>
            )}

            {/* Reviewer approval */}
            {status === "APPROVED" && (
              <Bubble side={reviewerSide}>
                <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3">
                  <div className="flex items-center gap-1 text-xs font-semibold text-emerald-700">
                    <CheckCircle2 size={13} /> {reviewerLabel} approved the design
                  </div>
                  {d.feedbackDate && (
                    <div className="mt-1 text-[11px] text-gray-400">{fmtDate(d.feedbackDate)}</div>
                  )}
                </div>
              </Bubble>
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
}

export default DesignConversation;
